import { useCallback, useEffect, useRef, type MutableRefObject } from 'react';
import type Konva from 'konva';
import type { CommandManager } from '../../../core/commands/CommandManager';
import { RefreshFlowchartTextLayoutCommand } from '../../../core/commands/RefreshFlowchartTextLayoutCommand';
import type { Node } from '../../../core/models/scene';
import {
  captureFlowchartTextLayout,
  isSameFlowchartTextLayout,
} from '../../../domains/diagram/shapes/flowchartTextLayout';
import { registerFlowchartTextLayoutRefresher } from '../../../domains/diagram/shapes/flowchartTextLayoutSync';

const resolveFlowchartLabel = (node: Node): string => {
  if (typeof node.text === 'string' && node.text.trim().length > 0) return node.text;
  return typeof node.attrs?.label === 'string' ? node.attrs.label : '';
};

export function useFlowchartTextLayoutSync({
  nodes,
  nodeRefs,
  commandManager,
  canWrite,
}: {
  nodes: Node[];
  nodeRefs: MutableRefObject<Map<string, Konva.Node>>;
  commandManager: CommandManager;
  canWrite: boolean;
}): void {
  const refreshInFlight = useRef<Promise<void> | null>(null);

  const refresh = useCallback(async () => {
    if (!canWrite) return;
    if (refreshInFlight.current) return refreshInFlight.current;

    const work = (async () => {
      const updates = nodes.flatMap((node) => {
        if (node.type !== 'flowchart-shape') return [];
        const label = resolveFlowchartLabel(node);
        const container = nodeRefs.current.get(node.id) as Konva.Container | undefined;
        const textNode = container?.findOne('Text') as Konva.Text | undefined;
        if (!label || !textNode) return [];

        const layout = captureFlowchartTextLayout(textNode, label);
        if (!layout || isSameFlowchartTextLayout(node.attrs?.flowchartTextLayout, layout)) return [];
        return [{ nodeId: node.id, layout }];
      });

      if (updates.length > 0) {
        await commandManager.execute(new RefreshFlowchartTextLayoutCommand(updates));
      }
    })();

    refreshInFlight.current = work;
    try {
      await work;
    } finally {
      refreshInFlight.current = null;
    }
  }, [canWrite, commandManager, nodeRefs, nodes]);

  useEffect(() => registerFlowchartTextLayoutRefresher(refresh), [refresh]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => void refresh());
    return () => cancelAnimationFrame(frame);
  }, [refresh]);
}
